/**
 * Trader profile quiz — behavioral dimensions and scoring.
 * Options are shuffled at quiz start using a persisted seed so reloads stay consistent.
 */

/** Gating only — not used in scoring (analytics / context). */
export const TRADER_SEGMENT_OPTIONS = [
  { id: 'scalp', label: 'Scalper' },
  { id: 'day', label: 'Day trader' },
  { id: 'swing', label: 'Swing trader (days to weeks)' },
  { id: 'long_term', label: 'Long-term investor' },
  { id: 'mixed', label: 'Mix of styles depending on the market' },
];

const TRADER_SEGMENT_LABEL_BY_ID = Object.fromEntries(
  TRADER_SEGMENT_OPTIONS.map((o) => [o.id, o.label]),
);

export function getTraderSegmentLabel(segmentId) {
  if (!segmentId) return null;
  return TRADER_SEGMENT_LABEL_BY_ID[segmentId] || null;
}

export const DIMENSION_KEYS = [
  'optimizer',
  'impulsive',
  'fearful',
  'fragile',
  'conviction',
];

export const DIMENSION_LABELS = {
  optimizer: 'Over-optimizing / strategy switching',
  impulsive: 'Impulse / overtrading',
  fearful: 'Hesitation / fear at execution',
  fragile: 'Plan drift under stress',
  conviction: 'Over-conviction / ego holding',
};

/** Shorter strings for the radar chart axis so labels are not clipped (full names still in tooltips). */
export const DIMENSION_RADAR_LABELS = {
  optimizer: 'Over-optimizing',
  impulsive: 'Impulse trading',
  fearful: 'Hesitation',
  fragile: 'Plan drift',
  conviction: 'Over-conviction',
};

const QUIZ_ARCHETYPE_IMAGE_FILES = {
  detached: 'detached.png',
  optimizer: 'optimizer.png',
  impulsive: 'impulsive.png',
  fearful: 'hesitator.png',
  fragile: 'fragile.png',
  conviction: 'riskmanagement.png',
};

export function getQuizArchetypeImageSrc(archetypeKey) {
  const file = QUIZ_ARCHETYPE_IMAGE_FILES[archetypeKey];
  return file ? `/images/quiz/${file}` : null;
}

/** Profile routes: `detached` is the aspirational archetype (threshold-based), not a scored dimension. */
export function isValidArchetypeKey(key) {
  return typeof key === 'string' && (key === 'detached' || DIMENSION_KEYS.includes(key));
}

/** Split archetype `body` copy into paragraphs (double newlines in source). */
export function splitArchetypeBodyParagraphs(body) {
  if (!body || typeof body !== 'string') return [];
  return body
    .split(/\n\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Order for `/quiz/profiles` — balanced archetype first, then dysfunction dimensions. */
export const PROFILE_CARD_ORDER_KEYS = ['detached', ...DIMENSION_KEYS];

export function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a += 0x6d2b79f5;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function shuffleArray(array, random = Math.random) {
  const out = [...array];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function zeroScores() {
  return Object.fromEntries(DIMENSION_KEYS.map((k) => [k, 0]));
}

export function computeMaxScoresPerDimension(questions) {
  const totals = zeroScores();
  for (const q of questions) {
    const m = zeroScores();
    for (const opt of q.options) {
      const s = opt.scores || {};
      for (const k of DIMENSION_KEYS) {
        const v = s[k] || 0;
        if (v > m[k]) m[k] = v;
      }
    }
    for (const k of DIMENSION_KEYS) totals[k] += m[k];
  }
  return totals;
}

export function prepareShuffledQuestions(questions, random = Math.random) {
  return questions.map((q) => ({
    ...q,
    options: shuffleArray(q.options, random),
  }));
}

export const QUIZ_QUESTIONS = [
  {
    id: 'after_two_losses',
    prompt: 'After two losses in a row, what do you actually do?',
    options: [
      { id: 'pause_review', text: 'Pause and review before taking another trade', scores: {} },
      { id: 'keep_trading', text: 'Keep trading normally', scores: { fragile: 2 } },
      {
        id: 'push_harder',
        text: 'Look for a strong setup to make it back quickly',
        scores: { impulsive: 2, fragile: 1 },
      },
      { id: 'change_strategy', text: 'Start adjusting or second-guessing my strategy', scores: { optimizer: 3 } },
      { id: 'hesitate_more', text: 'Become more hesitant to take the next trade', scores: { fearful: 3 } },
    ],
  },
  {
    id: 'valid_setup',
    prompt: 'You see a valid setup based on your plan. What happens?',
    options: [
      { id: 'take_it', text: 'Take it without hesitation', scores: {} },
      { id: 'hesitate', text: 'Hesitate or wait for more confirmation', scores: { fearful: 3 } },
      { id: 'skip', text: 'Sometimes skip it if I’m unsure', scores: { fearful: 2 } },
      { id: 'modify', text: 'Modify the setup slightly before entering', scores: { optimizer: 2 } },
      { id: 'jump_early', text: 'Enter early before confirmation', scores: { impulsive: 2 } },
    ],
  },
  {
    id: 'losing_trade',
    prompt: 'A trade is moving against you. What’s your instinct?',
    options: [
      { id: 'respect_stop', text: 'Let stop loss handle it', scores: {} },
      { id: 'cut_early', text: 'Exit early to avoid loss', scores: { fearful: 2 } },
      { id: 'widen_stop', text: 'Give it more room', scores: { conviction: 2 } },
      { id: 'add_position', text: 'Add to improve average', scores: { conviction: 3 } },
      { id: 'second_guess', text: 'Start questioning the whole trade idea', scores: { optimizer: 2 } },
    ],
  },
  {
    id: 'trade_frequency',
    prompt: 'How often do you take trades outside your defined setups?',
    options: [
      { id: 'rarely', text: 'Rarely', scores: {} },
      { id: 'sometimes', text: 'Sometimes', scores: { impulsive: 1 } },
      { id: 'often', text: 'Often', scores: { impulsive: 3 } },
      {
        id: 'depends_emotion',
        text: 'More when I’m frustrated or bored',
        scores: { fragile: 2, impulsive: 1 },
      },
    ],
  },
  {
    id: 'strategy_changes',
    prompt: 'How often do you change or tweak your strategy?',
    options: [
      { id: 'rare', text: 'Rarely—only after significant data', scores: {} },
      { id: 'sometimes', text: 'Occasionally', scores: { optimizer: 1 } },
      { id: 'frequent', text: 'Frequently', scores: { optimizer: 3 } },
      { id: 'after_losses', text: 'Usually after losses', scores: { optimizer: 2, fragile: 1 } },
    ],
  },
  {
    id: 'winning_trade',
    prompt: 'When you’re in a winning trade, what do you tend to do?',
    options: [
      { id: 'follow_plan', text: 'Follow my take profit plan', scores: {} },
      { id: 'exit_early', text: 'Take profits early', scores: { fearful: 2 } },
      { id: 'let_run', text: 'Let it run confidently', scores: {} },
      { id: 'add_more', text: 'Add to the position', scores: { conviction: 2 } },
      { id: 'overmanage', text: 'Constantly adjust exits', scores: { optimizer: 2 } },
    ],
  },
  {
    id: 'missed_move_reaction',
    prompt: 'You miss a clean move you were watching. What do you do?',
    options: [
      { id: 'move_on', text: 'Move on to the next opportunity', scores: {} },
      { id: 'chase', text: 'Chase the move late', scores: { impulsive: 3 } },
      {
        id: 'hesitate_then_chase',
        text: 'Hesitate, then enter late at a worse price',
        scores: { fearful: 2, impulsive: 1 },
      },
      { id: 'analyze_miss', text: 'Analyze why I missed it', scores: { optimizer: 2 } },
    ],
  },
  {
    id: 'session_end',
    prompt: 'You’re green on the day. What usually happens next?',
    options: [
      { id: 'stop', text: 'I stop trading and lock it in', scores: {} },
      { id: 'keep_trading', text: 'Keep trading, there’s more opportunity', scores: { impulsive: 2 } },
      { id: 'giveback', text: 'I often give some or all of it back', scores: { fragile: 3 } },
      { id: 'get_careful', text: 'I become overly cautious', scores: { fearful: 2 } },
    ],
  },
  {
    id: 'pre_trade_process',
    prompt: 'Before entering a trade, how consistent is your process?',
    options: [
      { id: 'very_consistent', text: 'Very consistent checklist/process', scores: {} },
      { id: 'mostly', text: 'Mostly consistent', scores: { fragile: 1 } },
      { id: 'rushed', text: 'Sometimes rushed or skipped', scores: { impulsive: 2, fragile: 1 } },
      { id: 'varies', text: 'Varies depending on the situation', scores: { optimizer: 2 } },
    ],
  },
  {
    id: 'confidence_vs_rules',
    prompt: 'How often does confidence override your rules?',
    options: [
      { id: 'rarely', text: 'Rarely', scores: {} },
      { id: 'sometimes', text: 'Sometimes', scores: { conviction: 1 } },
      { id: 'often', text: 'Often', scores: { conviction: 3 } },
      { id: 'depends_emotion', text: 'More when emotional', scores: { fragile: 2 } },
    ],
  },
  {
    id: 'market_activity',
    prompt: 'When the market is very active, how do you behave?',
    options: [
      { id: 'selective', text: 'Stay selective and stick to setups', scores: {} },
      { id: 'more_trades', text: 'Take more trades than usual', scores: { impulsive: 3 } },
      { id: 'overwhelmed', text: 'Feel overwhelmed and hesitate', scores: { fearful: 3 } },
      { id: 'adjust_strategy', text: 'Start adapting strategy in real time', scores: { optimizer: 2 } },
    ],
  },
  {
    id: 'losing_day_behavior',
    prompt: 'On a losing day, what’s most accurate?',
    options: [
      { id: 'stop_early', text: 'I cut it early and protect capital', scores: {} },
      { id: 'keep_pushing', text: 'I keep trading to recover', scores: { impulsive: 2, fragile: 2 } },
      {
        id: 'lose_confidence',
        text: 'I lose confidence in my strategy',
        scores: { optimizer: 2, fearful: 1 },
      },
      { id: 'hold_bad_trades', text: 'I hold onto bad trades too long', scores: { conviction: 3 } },
    ],
  },
];

const TIEBREAK_ORDER = ['fragile', 'impulsive', 'conviction', 'fearful', 'optimizer'];

export const ARCHETYPES = {
  optimizer: {
    key: 'optimizer',
    title: 'The Over-Optimizer',
    tagline: 'Always refining. Rarely committing.',
    intro:
      'You’re not lacking effort—you’re putting in more than most. The pattern here is that you refine, tweak, and adjust your strategy faster than you can actually validate it.',
    body:
      'This usually doesn’t come from randomness—it comes from trying to avoid being wrong. Small losing streaks feel like proof something is off, so you adjust before you’ve gathered enough data to know if the strategy actually works. Over time, this creates a loop where no strategy ever gets a fair test.\n\nThere’s also a subtle psychological safety in staying in “improvement mode”—you’re always close to solving it, but never fully exposed to being wrong on one approach.\n\nOn the surface, this looks like a strategy problem. In reality, it’s an execution problem—specifically, not committing long enough to let probability play out.',
    recognitions: [
      'Changing strategy after a small sample size',
      'Backtesting more than live trading',
      'Constantly refining entries/exits instead of executing',
      'Feeling close to “figuring it out” but never quite there',
      'Difficulty trusting one system through drawdowns',
    ],
    bodyClosing:
      'What you need isn’t a better strategy—it’s forced consistency. A system that limits unnecessary changes and lets you gather real data on performance. PrecisionTrader helps by reducing trade noise, tracking behavioral drift, and giving you visibility into whether your edge actually works—before you change it again.',
    cta:
      'If you keep changing the system, you’ll never know if it works. Lock in your rules and actually test them under real conditions.',
  },

  impulsive: {
    key: 'impulsive',
    title: 'The Impulse Hunter',
    tagline: 'Fast decisions. Loose filters.',
    intro:
      'You’re decisive, reactive, and engaged with the market. The issue isn’t hesitation—it’s that too many things qualify as “good enough” trades in the moment.',
    body:
      'This isn’t a knowledge problem—it’s a filtering problem. In fast markets, your brain shifts from executing a plan to reacting to movement. Setups blur together, and “maybe” trades start getting treated like A+ ones.\n\nThere’s also a momentum effect: once you start trading, it becomes harder to stop. Each trade pulls the next one in.\n\nOver time, this creates high activity but low signal—your edge gets buried under volume.',
    recognitions: [
      'Jumping into trades without full confirmation',
      'Taking trades outside your plan “because it looks good”',
      'Overtrading during active sessions',
      'Difficulty stopping once you’re in flow',
      'Green days that feel chaotic or inconsistent',
    ],
    bodyClosing:
      'You don’t need more opportunity—you need constraints. PrecisionTrader enforces hard limits on trade count, session timing, and activity so you’re forced to be selective instead of reactive.',
    cta:
      'If you don’t limit yourself, the market will keep pulling you into low-quality trades. Put hard caps in place before your edge gets buried in noise.',
  },

  fearful: {
    key: 'fearful',
    title: 'The Hesitant Executor',
    tagline: 'Sees the trade. Hesitates on execution.',
    intro:
      'You’re risk-aware and thoughtful—but that caution shows up right when it matters most: execution.',
    body:
      'You often recognize valid setups, but the moment of entry feels heavier than it should. Risk becomes more vivid, uncertainty expands, and you hesitate—or enter late when the edge is already weaker.\n\nThis creates a frustrating loop: you know what the trade was, you see it work without you, and confidence erodes slightly each time.\n\nIt’s not lack of knowledge—it’s friction at the point of execution.',
    recognitions: [
      'Missing trades you planned to take',
      'Late entries after hesitation',
      'Exiting winners too early',
      'Needing extra confirmation before acting',
      'Relief when skipped trades lose',
    ],
    bodyClosing:
      'You don’t need more analysis—you need smoother execution. PrecisionTrader reduces friction by automating sizing, defining risk instantly, and letting you act quickly without recalculating everything in the moment.',
    cta:
      'You already see the trades—execution is the bottleneck. Remove the friction so you can act without second-guessing every decision.',
  },

  fragile: {
    key: 'fragile',
    title: 'The Drifting Executor',
    tagline: 'Clear plan. Drifts under pressure.',
    intro:
      'You already know what you should be doing. The issue is that it doesn’t always hold up once you’re in a live session—especially after losses or emotional swings.',
    body:
      'This is one of the most common patterns among developing traders. You have structure, you have rules—but they rely on you executing perfectly in imperfect conditions.\n\nAfter a loss, frustration creeps in. After a win, overconfidence shows up.\n\nOver time, small deviations stack—extra trades, slight rule breaks, extended sessions—and performance drifts away from your actual strategy.',
    recognitions: [
      'Following your plan until a loss disrupts you',
      'Breaking rules after emotional swings',
      'Knowing you’re off-plan while doing it',
      'Giving back profits after strong starts',
      'Inconsistent performance across sessions',
    ],
    bodyClosing:
      'You don’t need more discipline—you need guardrails. PrecisionTrader enforces your rules automatically, so your worst moments don’t define your results.',
    cta:
      'If your discipline depends on how you feel, it will fail. Put systems in place that enforce your rules when you won’t.',
  },

  conviction: {
    key: 'conviction',
    title: 'The Conviction Trader',
    tagline: 'Strong ideas. Hard to let go.',
    intro:
      'You form strong trade ideas and trust your analysis. That conviction helps with winners—but it becomes dangerous when the trade is wrong.',
    body:
      'Once you’re in a position, it becomes harder to separate the idea from reality. Instead of exiting cleanly, the instinct is to give it more room, adjust the stop, or add to the position.\n\nThis isn’t random—it’s psychological commitment. You’ve already invested in the idea, and exiting feels like accepting loss not just financially, but mentally.\n\nThat’s when risk management quietly becomes negotiable—in the exact moments you need it to be absolute.',
    recognitions: [
      'Holding losers longer than planned',
      'Averaging down to defend trades',
      'Ignoring invalidation signals',
      'Sizing up based on conviction',
      'Large losses that outweigh smaller wins',
    ],
    bodyClosing:
      'You don’t need less conviction—you need limits that don’t change. PrecisionTrader enforces hard risk caps, required stops, and prevents adding to losing positions so conviction doesn’t override risk.',
    cta:
      'Conviction without limits turns into large losses. Define your risk upfront and make it non-negotiable.',
  },

  /**
   * Not a 6th scored dimension — assigned in `buildQuizResult` when max normalized dysfunction
   * across the five dimensions is below `DETACHED_THRESHOLD` (balanced / low signal on all).
   */
  detached: {
    key: 'detached',
    title: 'The Detached Professional',
    tagline: 'Executes the edge. Controls the process.',
    intro:
      'You operate with a level of structure and emotional control that most traders are still working toward. Decisions are driven more by process than impulse, and you’re able to execute without needing perfect conditions.',
    body:
      'You don’t rely on feeling right—you rely on having defined your edge ahead of time. Wins and losses don’t significantly change your behavior, which allows consistency to emerge over time.\n\nThis doesn’t mean you never make mistakes. It means mistakes don’t cascade. A loss stays a loss instead of turning into a sequence of bad decisions.\n\nThe key difference is separation: your identity isn’t tied to individual trades. You’re executing a system, not proving something.',
    recognitions: [
      'Following your plan regardless of recent outcomes',
      'Letting trades play out without interference',
      'Stable behavior across winning and losing days',
      'Minimal emotional impact from individual trades',
      'Consistent position sizing and risk control',
    ],
    bodyClosing:
      'At this level, the goal isn’t fixing major leaks—it’s maintaining consistency and refining your edge over time. PrecisionTrader helps by reinforcing structure, tracking behavior drift, and ensuring your execution stays aligned as conditions change.',
    cta:
      'Consistency is your edge—protect it. Keep your execution tight and your rules enforced as you scale.',
  },
};

/** Short blurbs for `/quiz/profiles` teaser cards. */
export const ARCHETYPE_PUBLIC_OVERVIEWS = {
  detached: {
    key: 'detached',
    headline: 'Balanced across themes',
    blurb:
      'No dysfunction theme dominates your answers—process, risk control, and emotional steadiness show through. The focus is maintaining edge and execution as you scale.',
  },
  optimizer: {
    key: 'optimizer',
    headline: 'Always refining the system',
    blurb:
      'You improve and tweak faster than you validate. Losing streaks trigger changes before you know if the strategy really works.',
  },
  impulsive: {
    key: 'impulsive',
    headline: 'Fast on the trigger',
    blurb:
      'You act on impulse; the hard part is filtering. In busy markets, too much looks good enough to trade.',
  },
  fearful: {
    key: 'fearful',
    headline: 'Execution lags behind recognition',
    blurb:
      'You see the setup, but commitment feels heavy—hesitation, late entries, or cutting winners early.',
  },
  fragile: {
    key: 'fragile',
    headline: 'Plan drifts in the session',
    blurb:
      'You know the rules when things are calm. After stress or emotion, small exceptions stack into drift.',
  },
  conviction: {
    key: 'conviction',
    headline: 'Sticks with the thesis',
    blurb:
      'You lean into your ideas—that’s often what makes winners work. The risk is when the trade is wrong but exiting feels like abandoning the story.',
  },
};

export const TOOL_BLURBS = {
  consecutive_loss_cooldown: {
    name: 'Consecutive loss cooldown',
    subtitle: 'Revenge trade breaker',
    description:
      'Pause after N losing trades in a row so you can’t immediately re-fire while heated.',
  },
  max_daily_loss: {
    name: 'Max daily loss',
    subtitle: 'Enforce or track',
    description: 'Stops new risk once today’s drawdown hits the line you set.',
  },
  manual_lockout: {
    name: 'Manual lockout',
    subtitle: 'Timed pause',
    description: 'Turn off new entries for a window you choose — no override in the moment.',
  },
  max_trades_per_day: {
    name: 'Max trades per day',
    subtitle: 'Cap activity',
    description: 'Forces a ceiling on how many times you can put on risk in a session.',
  },
  daily_profit_target: {
    name: 'Daily profit target',
    subtitle: 'Stop when you’re ahead',
    description: '“Done for the day” once you’ve hit the green zone you defined.',
  },
  trading_window: {
    name: 'Trading window',
    subtitle: 'Hours only',
    description: 'Blocks new trades outside the Eastern-time windows you allow.',
  },
  max_risk_per_trade: {
    name: 'Max risk per trade',
    subtitle: 'Size cap',
    description: 'Caps dollar risk per trade when stops or brackets define the risk.',
  },
  require_stop_loss: {
    name: 'Require stop loss',
    subtitle: 'No naked entries',
    description: 'New entries need a defined stop so risk isn’t “figure it out later.”',
  },
  block_adding_losers: {
    name: 'Block adding to losers',
    subtitle: 'No averaging down',
    description: 'Stops increasing size on positions that are already underwater.',
  },
  max_loss_position_alert: {
    name: 'Max loss per position (alert)',
    subtitle: 'Heads-up',
    description: 'Ping when a single position’s loss crosses a threshold — good paired with hard caps.',
  },
  stop_tp_tracking: {
    name: 'Stop / target change tracking',
    subtitle: 'Behavior visibility',
    description: 'Log how often stops or targets move after entry — patterns show up fast.',
  },
  journal_tracking: {
    name: 'Journal prompts',
    subtitle: 'Discipline layer',
    description: 'Lightweight journaling nudges tied to your session.',
  },
  auto_position_sizing: {
    name: 'Auto position sizing',
    subtitle: 'Risk + entry + stop',
    description:
      'Risk per trade + entry + stop loss is used to calculate position size; when you move the stop on the chart, size recalculates—less manual math between spotting the setup and sending the order.',
  },
  multi_tp_bracket_orders: {
    name: 'Multi take-profit bracket orders',
    subtitle: 'One submit, full plan',
    description:
      'Set entry, stop, and multiple profit targets in one bracket; drag levels on the chart, then scale out on autopilot without re-clicking every exit.',
  },
  chart_trading: {
    name: 'Chart trading',
    subtitle: 'Level to order on the chart',
    description:
      'Build and adjust entries, stops, and targets directly on price action so your workflow keeps up with what you see—faster from “that’s the trade” to risk-defined execution.',
  },
};

export const TOOLS_BY_ARCHETYPE = {
  detached: ['stop_tp_tracking', 'journal_tracking'],
  optimizer: ['journal_tracking', 'stop_tp_tracking', 'max_trades_per_day'],
  impulsive: ['max_trades_per_day', 'trading_window', 'journal_tracking'],
  fearful: ['auto_position_sizing', 'chart_trading', 'multi_tp_bracket_orders'],
  fragile: ['consecutive_loss_cooldown', 'max_daily_loss', 'manual_lockout', 'trading_window'],
  conviction: ['require_stop_loss', 'max_risk_per_trade', 'block_adding_losers', 'max_loss_position_alert'],
};

/** If max normalized score across dysfunction dimensions is at or below this, primary = Detached Professional. */
const DETACHED_THRESHOLD = 35;

const SECONDARY_THRESHOLD_PCT = 30;

function emptyScores() {
  return Object.fromEntries(DIMENSION_KEYS.map((k) => [k, 0]));
}

export function buildQuizResult(rawScores, maxScores) {
  const normalized = {};
  for (const k of DIMENSION_KEYS) {
    const denom = maxScores[k] || 1;
    normalized[k] = Math.min(100, Math.round(((rawScores[k] || 0) / denom) * 100));
  }

  const maxDysfunctionScore = Math.max(...DIMENSION_KEYS.map((k) => normalized[k]));

  let primary;
  if (maxDysfunctionScore <= DETACHED_THRESHOLD) {
    primary = 'detached';
  } else {
    let best = -1;
    primary = DIMENSION_KEYS[0];
    for (const k of DIMENSION_KEYS) {
      const v = normalized[k];
      if (v > best) {
        best = v;
        primary = k;
      }
    }
    const tied = DIMENSION_KEYS.filter((k) => normalized[k] === best);
    if (tied.length > 1) {
      primary = TIEBREAK_ORDER.find((k) => tied.includes(k)) || primary;
    }
  }

  const secondary = DIMENSION_KEYS.filter(
    (k) =>
      k !== primary &&
      normalized[k] >= SECONDARY_THRESHOLD_PCT &&
      maxScores[k] > 0,
  ).sort((a, b) => normalized[b] - normalized[a]);

  const maxRiskNorm = Math.max(...DIMENSION_KEYS.map((k) => normalized[k]));
  const encouragingContext = maxRiskNorm <= 40;

  return {
    rawScores: { ...emptyScores(), ...rawScores },
    normalized,
    primaryKey: primary,
    primary: ARCHETYPES[primary],
    secondaryKeys: secondary,
    encouragingContext,
    radarRows: DIMENSION_KEYS.map((key) => ({
      dimension: DIMENSION_RADAR_LABELS[key],
      dimensionFull: DIMENSION_LABELS[key],
      key,
      value: normalized[key],
      fullMark: 100,
    })),
    tools: (TOOLS_BY_ARCHETYPE[primary] || [])
      .map((id) => TOOL_BLURBS[id])
      .filter(Boolean),
  };
}

export function getMaxScores() {
  return computeMaxScoresPerDimension(QUIZ_QUESTIONS);
}